import keyBy from 'lodash-es/keyBy';
import omit from 'lodash-es/omit';
import orderBy from 'lodash-es/orderBy';
import {
  ITargetedCatalogGroupNormalized,
  ITargetedCatalogItem,
  ITargetedCatalogSection,
  ITargetedCatalogSectionNormalized,
  TargetedCatalogTypes
} from '.';
import { uniqPrimativeMerge } from '../../utils/arrayUtils';
import { ITargetedCatalogGroup } from './targetedCatalogInterfaces';

export interface ITargetedCatalogState {
  sections: Record<number, ITargetedCatalogSectionNormalized>;
  groups: Record<number, ITargetedCatalogGroupNormalized>;
  items: Record<number, ITargetedCatalogItem>;
  loadedAt: Date | undefined;
  isItemSearchLoading: boolean;
  searchResults: Record<number, Record<string, string[]>>;
  selectedSectionId: number;
}

const initState: ITargetedCatalogState = {
  groups: {},
  isItemSearchLoading: false,
  items: {},
  loadedAt: undefined,
  searchResults: {},
  sections: {},
  selectedSectionId: -1
};

export function targetedCatalogReducer(
  state: ITargetedCatalogState = initState,
  action: TargetedCatalogTypes
): ITargetedCatalogState {
  switch (action.type) {
    case 'REMOVE_TARGETED_CATALOG_ITEM':
      const groupId = state.items[action.payload.catalogItemId]
        ? state.items[action.payload.catalogItemId].preseasonCatalogItemGroupId
        : -1;
      return {
        ...state,
        groups: state.groups[groupId]
          ? {
              ...state.groups,
              [groupId]: {
                ...state.groups[groupId],
                productIds: state.groups[groupId].productIds.filter(x => x !== action.payload.catalogItemId)
              }
            }
          : state.groups,
        items: omit(state.items, action.payload.catalogItemId)
      };
    case 'REMOVE_TARGETED_CATALOG_SECTION':
      const sections = omit(state.sections, action.payload.sectionId);
      return {
        ...state,
        sections,
        selectedSectionId:
          action.payload.sectionId === state.selectedSectionId
            ? orderBy<ITargetedCatalogSectionNormalized>(Object.values(sections), x => x.order)[0]
                .preseasonCatalogSectionId
            : state.selectedSectionId
      };
    case 'REMOVE_TARGETED_ITEM_GROUP':
      const sectionId = state.groups[action.payload.groupId]
        ? state.groups[action.payload.groupId].preseasonCatalogSectionId
        : -1;
      const removeItemIds = state.groups[action.payload.groupId] ? state.groups[action.payload.groupId].productIds : [];
      return {
        ...state,
        groups: omit(state.groups, action.payload.groupId),
        items: omit(state.items, removeItemIds),
        sections: state.sections[sectionId]
          ? {
              ...state.sections,
              [sectionId]: {
                ...state.sections[sectionId],
                groupIds: state.sections[sectionId].groupIds.filter(x => x !== action.payload.groupId)
              }
            }
          : state.sections
      };
    case 'SET_TARGETED_CATALOG':
      return {
        ...state,
        ...normalizeSections(state, action.payload.sections),
        loadedAt: new Date(),
        selectedSectionId:
          action.payload.sections.length > 0
            ? orderBy<ITargetedCatalogSection>(action.payload.sections, x => x.order)[0].preseasonCatalogSectionId
            : -1
      };
    case 'SET_TARGETED_CATALOG_SECTION_MIN':
      return {
        ...state,
        sections: {
          ...state.sections,
          ...action.payload.reduce<Record<number, ITargetedCatalogSectionNormalized>>((hash, item) => {
            hash[item.preseasonCatalogSectionId] = {
              ...item,
              groupIds: state.sections[item.preseasonCatalogSectionId]
                ? state.sections[item.preseasonCatalogSectionId].groupIds
                : []
            };
            return hash;
          }, {})
        }
      };
    case 'SET_TARGETED_CATALOG_SELECTED_SECTION':
      return {
        ...state,
        selectedSectionId: action.payload.sectionId
      };
    case 'SET_TARGETED_ITEM_GROUPS':
      const { groups, items } = normalizeItemGroups(state, action.payload);
      return {
        ...state,
        groups: {
          ...state.groups,
          ...groups
        },
        items: {
          ...state.items,
          ...items
        },
        sections: {
          ...state.sections,
          ...action.payload.reduce<Record<number, ITargetedCatalogSectionNormalized>>((hash, item) => {
            const section = state.sections[item.preseasonCatalogSectionId];

            if (section) {
              hash[section.preseasonCatalogSectionId] = {
                ...section,
                groupIds: uniqPrimativeMerge(section.groupIds, item.preseasonCatalogItemGroupId)
              };
            }
            return hash;
          }, {})
        }
      };
    case 'SET_TARGETED_CATALOG_ITEM_SEARCH_RESULT':
      return {
        ...state,
        searchResults: {
          ...state.searchResults,
          [action.payload.sectionId]: {
            ...state.searchResults[action.payload.sectionId],
            [action.payload.searchTerm]: action.payload.itemIds
          }
        }
      };
    case 'TOGGLE_TARGETED_CATALOG_ITEM_SEARCH':
      return {
        ...state,
        isItemSearchLoading: !state.isItemSearchLoading
      };
    default:
      return state;
  }
}

function normalizeSections(
  state: ITargetedCatalogState,
  sections: ITargetedCatalogSection[]
): Pick<ITargetedCatalogState, 'groups' | 'items' | 'sections'> {
  const values: Pick<ITargetedCatalogState, 'groups' | 'items' | 'sections'> = {
    groups: { ...state.groups },
    items: { ...state.items },
    sections: { ...state.sections }
  };

  sections.forEach(x => {
    const currentSection = values.sections[x.preseasonCatalogSectionId] || {};
    const currentGroupIds = currentSection.groupIds || [];

    values.sections[x.preseasonCatalogSectionId] = {
      ...currentSection,
      ...omit<ITargetedCatalogSection>(x, ['groups']),
      groupIds: uniqPrimativeMerge(
        currentGroupIds,
        x.groups.map(z => z.preseasonCatalogItemGroupId)
      )
    };
    const { groups, items } = normalizeItemGroups(state, x.groups);
    values.groups = { ...values.groups, ...groups };
    values.items = { ...values.items, ...items };
  });

  return values;
}

function normalizeItemGroups(
  state: ITargetedCatalogState,
  groups: ITargetedCatalogGroup[]
): Pick<ITargetedCatalogState, 'groups' | 'items'> {
  const values: Pick<ITargetedCatalogState, 'groups' | 'items'> = {
    groups: { ...state.groups },
    items: { ...state.items }
  };

  groups.forEach(y => {
    const currentGroup = values.groups[y.preseasonCatalogItemGroupId] || {};
    const currentProductIds = currentGroup ? currentGroup.productIds : [];

    values.groups[y.preseasonCatalogItemGroupId] = {
      ...currentGroup,
      ...omit(y, 'products'),
      productIds: uniqPrimativeMerge(
        currentProductIds,
        y.products.map(z => z.preseasonCatalogItemId)
      )
    };
    values.items = { ...values.items, ...keyBy<ITargetedCatalogItem>(y.products, z => z.preseasonCatalogItemId) };
  });

  return values;
}
